import { Component, ErrorInfo, ReactNode } from 'react';
import { UpdateService } from '../../services/UpdateService';
import './DynamicImportErrorBoundary.scss';
import { UpdateNotification } from '../update-notification/UpdateNotification';

interface Props {
	children: ReactNode;
	fallback?: ReactNode;
}

interface State {
	hasError: boolean;
	error: Error | null;
	isUpdateAvailable: boolean;
}

/**
 * Error boundary component specifically designed to catch and handle errors
 * related to dynamic imports, which commonly occur after deployments when
 * cached module filenames no longer match the server's files.
 */
export class DynamicImportErrorBoundary extends Component<Props, State> {
	private readonly updateService: UpdateService;

	constructor(props: Props) {
		super(props);
		this.state = {
			hasError: false,
			error: null,
			isUpdateAvailable: false,
		};
		this.updateService = new UpdateService();
	}

	static getDerivedStateFromError(error: Error): State {
		// Update state so the next render will show the fallback UI
		return {
			hasError: true,
			error,
			isUpdateAvailable: false,
		};
	}

	componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
		// Log the error to the console
		console.error('Dynamic import error caught by boundary:', error, errorInfo);

		// Check if this is a chunk loading error (dynamic import failure)
		const isChunkLoadError = this.isChunkLoadError(error);

		if (isChunkLoadError) {
			console.log(
				'Detected chunk loading error, likely due to a new deployment'
			);
			// Set update available to trigger the update notification
			this.setState({ isUpdateAvailable: true });
		}
	}

	componentDidUpdate(prevProps: Props, prevState: State): void {
		// If we've determined an update is available and we weren't showing the error before
		if (this.state.isUpdateAvailable && !prevState.isUpdateAvailable) {
			// Force an update check
			this.updateService.forceUpdate().catch(error => {
				console.error('Failed to force update:', error);
			});
		}
	}

	render(): ReactNode {
		if (this.state.hasError) {
			// If we have a custom fallback, use it
			if (this.props.fallback) {
				return this.props.fallback;
			}

			// Otherwise, show our default error UI
			return <UpdateNotification />;
		}

		return this.props.children;
	}

	/**
	 * Determines if the error is related to chunk loading (dynamic import)
	 */
	private isChunkLoadError(error: Error): boolean {
		return (
			error.message.includes('Failed to fetch dynamically imported module') ||
			error.message.includes('Loading chunk') ||
			error.message.includes('Loading CSS chunk') ||
			error.message.includes('ChunkLoadError')
		);
	}

	/**
	 * Handle manual refresh attempt
	 */
	private readonly handleRefresh = (): void => {
		// Clear cache and reload the page
		if ('caches' in window) {
			void caches.keys().then(cacheNames => {
				cacheNames.forEach(cacheName => {
					void caches.delete(cacheName);
				});
				// window.location.reload();
			});
		} else {
			// window.location.reload();
		}
	};
}
